import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const AVAILABLE_ROLES = [
  { value: 'ROLE_ADMIN', label: 'Administrateur' },
  { value: 'ROLE_HOTESS', label: 'Hôtesse' },
  { value: 'ROLE_SELLER', label: 'Caissier' }
];

function Form({ open, onClose, initialData = null, onSubmit }) {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    roles: [],
    pin: '',
    password: ''
  });
  const [errors, setErrors] = React.useState({});
  const [generalError, setGeneralError] = React.useState(null);

  React.useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        email: initialData.email || '',
        roles: initialData.roles || [],
        pin: initialData.pin || '',
        password: initialData.password || ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        roles: [],
        pin: '',
        password: ''
      });
    }
    setErrors({});
    setGeneralError(null);
  }, [initialData, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setGeneralError(null);

    const errorCallback = (validationErrors) => {
      const errorsObj = {};
      let generalMessage = null;
      if (validationErrors && validationErrors.length > 0) {
        validationErrors.forEach(err => {
          if (err.field) {
            errorsObj[err.field] = err.message;
          } else {
            generalMessage = err.message || 'Une erreur est survenue';
          }
        });
      }
      setErrors(errorsObj);
      setGeneralError(generalMessage);
    };

    try {
      if (onSubmit) {
        await onSubmit({
          ...formData,
          id: initialData?.id
        }, errorCallback);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <span>{initialData ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</span>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 3 }}>
          {generalError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {generalError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Nom"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            size="small"
            error={!!errors.name}
            helperText={errors.name}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            required
            size="small"
            error={!!errors.email}
            helperText={errors.email}
          />

          <TextField
            fullWidth
            label="Mot de passe"
            type="password"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            required={!initialData}
            size="small"
            error={!!errors.password}
            helperText={errors.password}
          />

          <FormControl fullWidth size="small" error={!!errors.roles}>
            <InputLabel>Rôles</InputLabel>
            <Select
              multiple
              value={formData.roles}
              onChange={(e) => handleChange('roles', e.target.value)}
              required
            >
              {AVAILABLE_ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
            {errors.roles && <FormHelperText>{errors.roles}</FormHelperText>}
          </FormControl>

          <TextField
            fullWidth
            label="PIN (optionnel)"
            type="number"
            value={formData.pin}
            onChange={(e) => handleChange('pin', e.target.value)}
            size="small"
            error={!!errors.pin}
            helperText={errors.pin}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" variant="contained">
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    email: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string),
    pin: PropTypes.string,
    password: PropTypes.string
  })
};

export default Form; 